import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import technologyData from "../../JsonFiles/Technologies.json";
import "./TechStack.css";

const CATEGORY_CONFIG = [
  {
    key: "web-development",
    fields: ["Web Development"],
    title: "Web Development",
    description: "Modern frontend and backend technologies for fast, reliable web products.",
  },
  {
    key: "mobile-application",
    fields: ["Mobile Application", "Mobile Applications"],
    title: "Mobile Applications",
    description: "Flexible technology choices for polished Android, iOS, and cross-platform apps.",
  },
  {
    key: "databases-apis",
    fields: ["Databases & Api's", "Databases & APIs", "Database & APIs"],
    title: "Databases & APIs",
    description: "Secure data, integration, and API foundations designed for dependable scale.",
  },
];

const ScrollLink = ({ to, children, ...props }) => {
  const handleClick = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <RouterLink to={to} onClick={handleClick} {...props}>
      {children}
    </RouterLink>
  );
};

const TechnologyCard = ({ technology }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const title = String(technology.title ?? "Technology");
  const fallback = title.replace(/[^a-zA-Z0-9]/g, "").slice(0, 2).toUpperCase() || "AX";

  return (
    <ScrollLink
      to={`/technologies/${technology.id}`}
      className="tech-stack__card"
      aria-label={`Explore ${title}`}
    >
      <span className="tech-stack__icon" aria-hidden="true">
        {technology.icon && !imageFailed ? (
          <img
            src={technology.icon}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <span>{fallback}</span>
        )}
      </span>

      <span className="tech-stack__card-title">{title}</span>

      <span className="tech-stack__card-arrow" aria-hidden="true">
        <FontAwesomeIcon icon={faArrowRight} />
      </span>
    </ScrollLink>
  );
};

const TechStack = ({ id = "technologies" }) => {
  const categories = CATEGORY_CONFIG.map((category) => ({
    ...category,
    technologies: technologyData.filter((technology) =>
      category.fields.includes(technology.field),
    ),
  })).filter((category) => category.technologies.length > 0);

  return (
    <section id={id} className="tech-stack" aria-labelledby="tech-stack-title">
      <div className="tech-stack__decoration" aria-hidden="true" />

      <div className="tech-stack__container">
        <header className="tech-stack__header">
          <div>
            <p className="tech-stack__eyebrow">
              <span aria-hidden="true" />
              Technology toolkit
            </p>

            <h2 id="tech-stack-title" className="tech-stack__title">
              Modern technologies.
              <span> Built for real-world scale.</span>
            </h2>
          </div>

          <div className="tech-stack__header-side">
            <p>
              We select technologies around your product requirements—not trends—so
              every solution remains secure, maintainable, and ready to evolve.
            </p>

            <ScrollLink className="tech-stack__all-link" to="/technologies">
              Explore all technologies
              <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </ScrollLink>
          </div>
        </header>

        <div className="tech-stack__categories">
          {categories.map((category, categoryIndex) => (
            <section
              key={category.key}
              className="tech-stack__category"
              aria-labelledby={`tech-category-${category.key}`}
            >
              <header className="tech-stack__category-header">
                <div className="tech-stack__category-number" aria-hidden="true">
                  {String(categoryIndex + 1).padStart(2, "0")}
                </div>

                <div>
                  <h3 id={`tech-category-${category.key}`}>{category.title}</h3>
                  <p>{category.description}</p>
                </div>

                {/* <span className="tech-stack__count">
                  {category.technologies.length}{" "}
                  {category.technologies.length === 1 ? "technology" : "technologies"}
                </span> */}
              </header>

              <div className="tech-stack__grid">
                {category.technologies.map((technology, index) => (
                  <TechnologyCard
                    key={technology.id ?? `${category.key}-${index}`}
                    technology={technology}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>

        <aside className="tech-stack__note" aria-label="Technology selection approach">
          <span>Technology-agnostic approach</span>
          <p>
            Your business goals determine the stack. Our engineering standards make
            it reliable.
          </p>
        </aside>
      </div>
    </section>
  );
};

export default TechStack;
